import {
  Box,
  Input,
  ModeSection,
  PillGroup,
  RadioCards,
  SectionLabel,
  Slider,
  TierBadge,
  Toggle,
} from '@figma-importers/ui';
import { ArrowDownUp, ArrowRightLeft, LayoutGrid } from 'lucide-react';
import { useImportStore } from '../store/importStore';
import { FontMatching } from './FontMatching';
import type { ImportMode } from '../types/messages';

/*
 * EDITABLE FIRST, IMAGE LAST — the order and the words pdf-to-figma uses, because the two
 * plugins ask the same question and a designer moving between them should not have to re-read
 * it. This list used to run the other way and call the editable one "Live text".
 *
 * The descriptions stay this app's own: what is editable here is vectors PLUS text, and the
 * fonts are matched rather than embedded, which is a thing worth saying about an .ai file and
 * is not true in the same way of a PDF.
 */
const MODES: { value: ImportMode; label: string; description: string }[] = [
  {
    value: 'text',
    label: 'Editable',
    description: 'Vectors plus real, editable Figma text layers (fonts matched, not embedded).',
  },
  {
    value: 'vector',
    label: 'Vector',
    description: 'Vector paths you can edit — best for simpler artboards.',
  },
  {
    value: 'image',
    label: 'Image',
    description: 'Pixel-perfect raster of each artboard — works for any file.',
  },
];
// n× maps directly to DPI for PDF user space (1/72 inch). Engine clamps to 4096 px.
export function ModePicker() {
  const mode = useImportStore((s) => s.mode);
  const scale = useImportStore((s) => s.scale);
  const flatten = useImportStore((s) => s.flatten);
  const setMode = useImportStore((s) => s.setMode);
  const setScale = useImportStore((s) => s.setScale);
  const setFlatten = useImportStore((s) => s.setFlatten);
  const probe = useImportStore((s) => s.probe);
  const selection = useImportStore((s) => s.selection);
  const range = useImportStore((s) => s.range);
  const layout = useImportStore((s) => s.layout);
  const setSelection = useImportStore((s) => s.setSelection);
  const setRange = useImportStore((s) => s.setRange);
  const setLayout = useImportStore((s) => s.setLayout);
  const gate = useImportStore((s) => s.gate);
  // A paid licence, not beta: beta allows everything else, so this is what it buys today.
  const isPro = gate.plan === 'pro';

  const count = probe?.pageCount ?? 0;
  /*
   * Whether importing the whole file costs an upgrade. `pageLimit` is how many artboards one
   * free import may carry; a file within it needs no badges at all, and showing PRO next to
   * something already included would be a lie told for emphasis.
   */
  const limit = gate.pageLimit;
  /*
   * Image mode is free on every plan, so it carries no badges and no "First artboard" row —
   * the same as pdf's Image tab. Without the mode in this condition the radio advertised a
   * price while the footer beneath it, which does account for mode, offered a plain Import.
   */
  const overLimit = mode !== 'image' && Number.isFinite(limit) && count > limit;


  return (
    <Box display="flex" flexDirection="column" gap="3.5">
      <ModeSection modes={MODES} value={mode} onChange={setMode} />

      {/*
        Which artboards — only worth asking when there is more than one, and only answerable
        once the probe has counted them. A single-artboard file has nothing to choose.
      */}
      {count > 1 && (
        <Box>
          <SectionLabel>Which artboards?</SectionLabel>
          <RadioCards
            value={selection}
            onChange={setSelection}
            name="artboard-selection"
            options={[
              ...(overLimit
                ? [{ value: 'first' as const, label: 'First artboard', trailing: <TierBadge tier="free" /> }]
                : []),
              {
                value: 'all' as const,
                label: `Every artboard (${count})`,
                trailing: overLimit ? <TierBadge tier="pro" /> : null,
              },
              {
                value: 'range' as const,
                label: 'Pick a few',
                trailing: overLimit ? <TierBadge tier="pro" /> : null,
              },
            ]}
          />
          {selection === 'range' && (
            <Box mt="1.5">
              <Input
                value={range}
                onChange={setRange}
                placeholder={`e.g. 1-3, ${count}`}
                size="xs"
              />
            </Box>
          )}
        </Box>
      )}

      {/* How they are arranged once placed. Meaningless for a single artboard. */}
      {count > 1 && (
        <Box>
          <SectionLabel>Lay artboards out</SectionLabel>
          <PillGroup
            value={layout}
            onChange={setLayout}
            options={[
              { value: 'row', label: 'Side by side', icon: <ArrowRightLeft size={10} /> },
              { value: 'column', label: 'Stacked', icon: <ArrowDownUp size={10} /> },
              { value: 'grid', label: 'Grid', icon: <LayoutGrid size={10} /> },
            ]}
          />
        </Box>
      )}

      {/*
        pdf's quality control: label, track and value on one row, tucked under the mode
        description rather than given a section of its own.
        
        It reads in MULTIPLES, not percent, and that is not a cosmetic difference. pdf's
        slider is 10-100% of full quality — a downscale, where 100 is best. This one is a
        supersampling factor where 1x is native and 4x is sharpest. Labelling it "100%"
        would name illustrator's LOWEST setting with pdf's word for its highest.
      */}
      {mode === 'image' && (
        <Slider
          inline
          label="Quality"
          value={scale}
          onChange={setScale}
          min={1}
          max={4}
          step={1}
          valueLabel={`${scale}\u00d7 \u00b7 ~${scale * 72} DPI`}
          ariaLabel="Image quality"
        />
      )}

      {/*
        Pro only, and `plan === 'pro'` rather than "not free" on purpose: beta is unlimited
        for everything else, so this is the one setting a licence actually buys while the
        beta runs. Rendering `isPro && flatten` rather than `flatten` means a lapsed licence
        shows the setting off, which is what the import will do with it.
      */}
      {/* Editable only: vector outlines its glyphs and image rasterises them, so in
          neither does a font name survive for a mapping to apply to. */}
      {mode === 'text' && <FontMatching />}

      <Toggle
        checked={isPro && flatten}
        onChange={setFlatten}
        disabled={!isPro}
        trailing={isPro ? null : <TierBadge tier="pro" />}
        label="Flatten transparency & effects"
        description={
          isPro
            ? 'Slower, but more faithful for soft masks, blend modes, overprint'
            : 'More faithful for soft masks, blend modes and overprint — included with Pro'
        }
      />
    </Box>
  );
}
